import { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import type { GridPaginationModel } from "@mui/x-data-grid-premium";
import dayjs, { type Dayjs } from "dayjs";
import { useAiHubListFiltersStore } from "../aiHubListFiltersStore";
import {
  fetchOtoActivePlans,
  fetchOtoAssessmentPairs,
  fetchOtoAssessments,
  fetchOtoRelevanceThresholds,
  type OtoRelevanceThresholds,
} from "../../../services/objectToObjectAssessmentService";
import ObjectAnalysisFilterBar, {
  type ObjectAnalysisView,
} from "./ObjectAnalysisFilterBar";
import ObjectAnalysisPairTable from "./ObjectAnalysisPairTable";
import ObjectAnalysisSummaryTable from "./ObjectAnalysisSummaryTable";
import {
  toObjectAnalysisPlan,
  toPairRow,
  toSummaryRow,
  type ObjectAnalysisPairRow,
  type ObjectAnalysisPlan,
  type ObjectAnalysisSummaryRow,
} from "./objectAnalysisData";

const DEFAULT_THRESHOLDS: OtoRelevanceThresholds = {
  high: 0.67,
  medium: 0.34,
};

function toUtcIso(
  fromIsoValue: string | null,
  toIsoValue: string | null,
): {
  createdFrom: string | undefined;
  createdTo: string | undefined;
} {
  return {
    createdFrom: fromIsoValue
      ? dayjs(fromIsoValue).startOf("day").toDate().toISOString()
      : undefined,
    createdTo: toIsoValue
      ? dayjs(toIsoValue).endOf("day").toDate().toISOString()
      : undefined,
  };
}

function fromIso(value: string | null): Dayjs | null {
  return value ? dayjs(value) : null;
}

export default function ObjectAnalysisTab() {
  const { planIds, fromDateIso, toDateIso, view, page, pageSize } =
    useAiHubListFiltersStore((state) => state.objectAnalysis);
  const setObjectAnalysis = useAiHubListFiltersStore(
    (state) => state.setObjectAnalysis,
  );
  const fromDate = useMemo(() => fromIso(fromDateIso), [fromDateIso]);
  const toDate = useMemo(() => fromIso(toDateIso), [toDateIso]);
  const paginationModel = useMemo<GridPaginationModel>(
    () => ({ page, pageSize }),
    [page, pageSize],
  );

  const [plans, setPlans] = useState<ObjectAnalysisPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState<string | null>(null);

  const [thresholds, setThresholds] =
    useState<OtoRelevanceThresholds>(DEFAULT_THRESHOLDS);

  const [summaryRows, setSummaryRows] = useState<ObjectAnalysisSummaryRow[]>([]);
  const [pairRows, setPairRows] = useState<ObjectAnalysisPairRow[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const selectedPlans = useMemo(
    () => plans.filter((plan) => planIds.includes(plan.id)),
    [planIds, plans],
  );

  useEffect(() => {
    let cancelled = false;
    setPlansLoading(true);
    setPlansError(null);
    Promise.all([fetchOtoActivePlans(), fetchOtoRelevanceThresholds()])
      .then(([activePlans, nextThresholds]) => {
        if (cancelled) return;
        setPlans(activePlans.map(toObjectAnalysisPlan));
        setThresholds(nextThresholds);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setPlans([]);
        setPlansError(
          err instanceof Error ? err.message : "Failed to load assessment plans.",
        );
      })
      .finally(() => {
        if (!cancelled) setPlansLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    const { createdFrom, createdTo } = toUtcIso(fromDateIso, toDateIso);
    const page = paginationModel.page + 1;
    const pageSize = paginationModel.pageSize;

    try {
      if (view === "grid") {
        if (selectedPlans.length <= 1) {
          const result = await fetchOtoAssessments({
            page,
            pageSize,
            objectAType: selectedPlans[0]?.objectAType,
            objectBType: selectedPlans[0]?.objectBType,
            createdFrom,
            createdTo,
          });
          setSummaryRows(result.items.map((item) => toSummaryRow(item, plans)));
          setPairRows([]);
          setRowCount(result.total);
          return;
        }

        const pages = await Promise.all(
          selectedPlans.map((plan) =>
            fetchOtoAssessments({
              page: 1,
              pageSize: 100,
              objectAType: plan.objectAType,
              objectBType: plan.objectBType,
              createdFrom,
              createdTo,
            }),
          ),
        );
        const merged = new Map<string, ObjectAnalysisSummaryRow>();
        for (const pageResult of pages) {
          for (const item of pageResult.items) {
            const row = toSummaryRow(item, plans);
            merged.set(row.id, row);
          }
        }
        const allRows = [...merged.values()];
        const start = paginationModel.page * paginationModel.pageSize;
        setSummaryRows(allRows.slice(start, start + paginationModel.pageSize));
        setPairRows([]);
        setRowCount(allRows.length);
        return;
      }

      if (selectedPlans.length <= 1) {
        const plan = selectedPlans[0];
        const result = await fetchOtoAssessmentPairs({
          page,
          pageSize,
          objectAType: plan?.objectAType,
          objectBType: plan?.objectBType,
          createdFrom,
          createdTo,
        });
        setPairRows(
          result.items.map((item) => toPairRow(item, thresholds, plans)),
        );
        setSummaryRows([]);
        setRowCount(result.total);
        return;
      }

      const pages = await Promise.all(
        selectedPlans.map((plan) =>
          fetchOtoAssessmentPairs({
            page: 1,
            pageSize: 100,
            objectAType: plan.objectAType,
            objectBType: plan.objectBType,
            createdFrom,
            createdTo,
          }),
        ),
      );
      const merged = new Map<string, ObjectAnalysisPairRow>();
      for (const pageResult of pages) {
        for (const item of pageResult.items) {
          const row = toPairRow(item, thresholds, plans);
          merged.set(row.id, row);
        }
      }
      const allRows = [...merged.values()];
      const start = paginationModel.page * paginationModel.pageSize;
      setPairRows(allRows.slice(start, start + paginationModel.pageSize));
      setSummaryRows([]);
      setRowCount(allRows.length);
    } catch (err: unknown) {
      setSummaryRows([]);
      setPairRows([]);
      setRowCount(0);
      setListError(
        err instanceof Error ? err.message : "Failed to load analysis results.",
      );
    } finally {
      setListLoading(false);
    }
  }, [
    fromDateIso,
    paginationModel.page,
    paginationModel.pageSize,
    plans,
    selectedPlans,
    thresholds,
    toDateIso,
    view,
  ]);

  useEffect(() => {
    if (plansLoading) return;
    void loadList();
  }, [loadList, plansLoading]);

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      if (model.page === page && model.pageSize === pageSize) return;
      setObjectAnalysis({ page: model.page, pageSize: model.pageSize });
    },
    [page, pageSize, setObjectAnalysis],
  );

  const handlePlanIdsChange = useCallback(
    (next: string[]) => {
      setObjectAnalysis({ planIds: next, page: 0 });
    },
    [setObjectAnalysis],
  );

  const handleDateRangeChange = useCallback(
    (from: Dayjs | null, to: Dayjs | null) => {
      setObjectAnalysis({
        fromDateIso: from ? from.toISOString() : null,
        toDateIso: to ? to.toISOString() : null,
        page: 0,
      });
    },
    [setObjectAnalysis],
  );

  const handleViewChange = useCallback(
    (nextView: ObjectAnalysisView) => {
      setObjectAnalysis({ view: nextView, page: 0 });
    },
    [setObjectAnalysis],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        flex: 1,
        minHeight: 0,
        mx: 0,
      }}
    >
      <ObjectAnalysisFilterBar
        plans={plans}
        plansLoading={plansLoading}
        planIds={planIds}
        onPlanIdsChange={handlePlanIdsChange}
        fromDate={fromDate}
        toDate={toDate}
        onDateRangeChange={handleDateRangeChange}
        view={view}
        onViewChange={handleViewChange}
      />

      {plansError ? (
        <Typography sx={{ color: "#CF1322", fontSize: 14 }}>{plansError}</Typography>
      ) : null}
      {listError ? (
        <Typography sx={{ color: "#CF1322", fontSize: 14 }}>{listError}</Typography>
      ) : null}

      {plansLoading || listLoading ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 280,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={32} />
        </Box>
      ) : view === "grid" ? (
        <ObjectAnalysisSummaryTable
          rows={summaryRows}
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
        />
      ) : (
        <ObjectAnalysisPairTable
          rows={pairRows}
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
        />
      )}
    </Box>
  );
}
